/**
 * Zod schemas for the voice_note extension (spoken notes + synced cursor track).
 */
import { z } from "zod"

import { decodeVoiceNoteFile, type VoiceNoteFileV1 } from "./file.ts"

/** Template schema: the module has no knobs; recording is unlimited by design. */
export const voiceNoteConfigSchema = z.object({
  type: z.literal("voice_note")
})

export type VoiceNoteConfig = z.infer<typeof voiceNoteConfigSchema>

/**
 * Create payload: the encoded `VoiceNoteFileV1` bytes. Sent inside an
 * `application/cbor` request body so the audio never round-trips through
 * base64. Validation decodes the file once and hands the decoded note to
 * `serializeForDb` alongside the original bytes. The output also carries the
 * file's `seriesId` as `series_id` so the generic create route stores the
 * annotation under that series (and rejects an envelope naming another one).
 */
export const createVoiceNoteSchema = z
  .object({
    file: z.instanceof(Uint8Array)
  })
  .transform((value, ctx): { file: Uint8Array; note: VoiceNoteFileV1; series_id: string } => {
    const note = decodeVoiceNoteFile(value.file)
    if (!note) {
      ctx.addIssue({ code: "custom", path: ["file"], message: "file is not a valid voice note (v1) CBOR document" })
      return z.NEVER
    }
    return { file: value.file, note, series_id: note.seriesId }
  })

export type CreateVoiceNoteInput = z.output<typeof createVoiceNoteSchema>

/** Voice notes are immutable in v1: delete and re-record instead. */
export const updateVoiceNoteSchema = z.custom<never>(() => false, {
  message: "voice_note annotations cannot be updated; delete and re-record instead"
})
